import { Trans } from "@lingui/react/macro"
import { queryOptions, useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Download, Eye, FileIcon, Store, Calendar } from "lucide-react"
import { Button } from "@/components/ui/button"
import { SharedFileError } from "@/features/store-files/components/SharedFileError"
import { getShareByToken } from "@/features/store-files/server/fileServer"

const sharedFileQuery = (token: string) =>
  queryOptions({
    queryKey: ["shared-file", token],
    queryFn: () => getShareByToken({ data: token }),
    retry: false,
  })

export const Route = createFileRoute("/shared/$token")({
  loader: async ({ context, params }) => {
    try {
      return await context.queryClient.fetchQuery(sharedFileQuery(params.token))
    } catch {
      // Will be handled by error component
      return null
    }
  },
  component: SharedFilePage,
  errorComponent: ({ error }) => <SharedFileError error={error instanceof Error ? error : null} />,
})

function SharedFilePage() {
  const { token } = Route.useParams()
  const { data, error, isLoading } = useQuery(sharedFileQuery(token))

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-neutral-300 border-t-blue-600" />
          <p className="mt-4 text-neutral-600 dark:text-neutral-400">
            <Trans>Loading shared file...</Trans>
          </p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return <SharedFileError error={error} />
  }

  const handleDownload = () => {
    window.location.href = `/api/shared/${token}/download`
  }

  const handleView = () => {
    window.open(`/api/shared/${token}/view`, "_blank")
  }

  // Check if file can be viewed in browser
  const isViewable = isViewableContentType(data.file.contentType)

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 p-4 dark:bg-neutral-900">
      <div className="w-full max-w-md rounded-lg border border-neutral-200 bg-white p-6 shadow-lg dark:border-neutral-700 dark:bg-neutral-800">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
            <FileIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-xl font-semibold text-neutral-900 dark:text-white">
            <Trans>Shared File</Trans>
          </h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            <Trans>Someone shared a file with you</Trans>
          </p>
        </div>

        {/* Note (if present) */}
        {data.note && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
            <p className="text-sm text-blue-800 dark:text-blue-200 whitespace-pre-wrap">
              {data.note}
            </p>
          </div>
        )}

        {/* File info */}
        <div className="mb-6 space-y-3 rounded-lg bg-neutral-50 p-4 dark:bg-neutral-900/50">
          <div className="flex items-start gap-3">
            <FileIcon className="mt-0.5 h-5 w-5 shrink-0 text-neutral-400" />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-neutral-900 dark:text-white break-all">
                {data.file.fileName}
              </p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                {formatFileSize(data.file.size)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Store className="h-5 w-5 shrink-0 text-neutral-400" />
            <p className="text-sm text-neutral-600 dark:text-neutral-300">{data.file.storeName}</p>
          </div>

          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 shrink-0 text-neutral-400" />
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              <Trans>
                Expires{" "}
                {new Intl.DateTimeFormat(undefined, {
                  dateStyle: "medium",
                  timeStyle: "short",
                }).format(new Date(data.expiresAt))}
              </Trans>
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          {isViewable && (
            <Button onClick={handleView} variant="outline" className="flex-1 gap-2" size="lg">
              <Eye className="h-5 w-5" />
              <Trans>View</Trans>
            </Button>
          )}
          <Button onClick={handleDownload} className="flex-1 gap-2" size="lg">
            <Download className="h-5 w-5" />
            <Trans>Download</Trans>
          </Button>
        </div>

        {/* Footer */}
        <p className="mt-4 text-center text-xs text-neutral-500 dark:text-neutral-400">
          <Trans>This link will expire and the file will no longer be accessible.</Trans>
        </p>
      </div>
    </div>
  )
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isViewableContentType(contentType: string): boolean {
  // Content types that browsers can typically display inline
  const viewableTypes = [
    "application/pdf",
    "text/plain",
    "text/html",
    "text/css",
    "text/javascript",
    "application/json",
    "application/xml",
    "text/xml",
  ]

  return (
    contentType.startsWith("image/") ||
    contentType.startsWith("video/") ||
    contentType.startsWith("audio/") ||
    viewableTypes.includes(contentType)
  )
}
